// AdminFeesManager.jsx (Complete with Edit Functionality)
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import jsPDF from 'jspdf';
import SideBar from '../admincomponents/SideBar';
import TopNavBar from '../admincomponents/TopNavBar';
import Footer from '../admincomponents/Footer';

const apiUrl = process.env.REACT_APP_API_URL;

function AdminFeesManager() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [courseFilter, setCourseFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showHidden, setShowHidden] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ courseId: '', amountpaid: '', modeofpayment: '', remark: '' });
  const [discountForm, setDiscountForm] = useState({ discountAmount: '', remark: '' });
  const [paymentError, setPaymentError] = useState('');
  const [paymentSuccess, setPaymentSuccess] = useState('');
  const [discountError, setDiscountError] = useState('');
  const [discountSuccess, setDiscountSuccess] = useState('');
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [discountHistory, setDiscountHistory] = useState([]);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [discountModalOpen, setDiscountModalOpen] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  
  // Edit Payment States
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({ 
    transactionId: '', 
    amountpaid: '', 
    modeofpayment: '', 
    remark: '' 
  });
  const [editError, setEditError] = useState('');
  const [editSuccess, setEditSuccess] = useState('');
  const [selectedPayment, setSelectedPayment] = useState(null);
  
  // Edit Discount States
  const [editDiscountModalOpen, setEditDiscountModalOpen] = useState(false);
  const [editDiscountForm, setEditDiscountForm] = useState({ 
    discountId: '', 
    discountAmount: '', 
    remark: '' 
  });
  const [editDiscountError, setEditDiscountError] = useState('');
  const [editDiscountSuccess, setEditDiscountSuccess] = useState('');
  const [selectedDiscount, setSelectedDiscount] = useState(null);

  const fetchStudents = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
      if (!sessionData || !sessionData.token) {
        setError('You must be logged in to view fees manager.');
        setLoading(false);
        return;
      }
      const response = await axios.get(
        `${apiUrl}/api/admin/fees-manager?showHidden=${showHidden ? 1 : 0}&nocache=${Date.now()}`,
        { headers: { Authorization: `Bearer ${sessionData.token}` } }
      );
      if (response.data.success) {
        const processedStudents = response.data.students.map(student => ({
          ...student,
          originalFee: Number(student.originalFee) || 0,
          discountAmount: Number(student.discountAmount) || 0,
          feesPaid: Number(student.feesPaid) || 0,
          balance: Number(student.balance) || 0
        }));
        setStudents(processedStudents);
      } else {
        setError(response.data.message || 'Failed to fetch students.');
      }
    } catch (err) {
      console.error('Error fetching students:', err);
      setError(err.response?.data?.message || 'Error fetching students. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [showHidden]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  const openPaymentModal = (student) => {
    setSelectedStudent(student);
    setPaymentForm({ courseId: student.courseid, amountpaid: '', modeofpayment: '', remark: '' });
    setPaymentError('');
    setPaymentSuccess('');
    setPaymentModalOpen(true);
  };

  const closePaymentModal = () => {
    setPaymentModalOpen(false);
  };

  const openDiscountModal = (student) => {
    setSelectedStudent(student);
    setDiscountForm({ discountAmount: '', remark: '' });
    setDiscountError('');
    setDiscountSuccess('');
    setDiscountModalOpen(true);
  };

  const closeDiscountModal = () => {
    setDiscountModalOpen(false);
  };

  const openHistoryModal = (student) => {
    setSelectedStudent(student);
    setHistoryModalOpen(true);
    fetchPaymentHistory(student.studentid, student.courseid);
  };

  const closeHistoryModal = () => {
    setHistoryModalOpen(false);
  };

  // Edit Payment Functions
  const openEditModal = (payment) => {
    setSelectedPayment(payment);
    setEditForm({
      transactionId: payment.transactionid || payment.id,
      amountpaid: payment.amountpaid,
      modeofpayment: payment.modeofpayment,
      remark: payment.remark || ''
    });
    setEditError('');
    setEditSuccess('');
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setSelectedPayment(null);
  };

  const handleEditChange = (e) => {
    const { id, value } = e.target;
    setEditForm((prev) => ({ ...prev, [id]: value }));
    setEditError('');
  };

  // Edit Discount Functions
  const openEditDiscountModal = (discount) => {
    setSelectedDiscount(discount);
    setEditDiscountForm({
      discountId: discount.id,
      discountAmount: discount.discount_amount,
      remark: discount.remark || ''
    });
    setEditDiscountError('');
    setEditDiscountSuccess('');
    setEditDiscountModalOpen(true);
  };

  const closeEditDiscountModal = () => {
    setEditDiscountModalOpen(false);
    setSelectedDiscount(null);
  };

  const handleEditDiscountChange = (e) => {
    const { id, value } = e.target;
    setEditDiscountForm((prev) => ({ ...prev, [id]: value }));
    setEditDiscountError('');
  };

  const fetchPaymentHistory = async (studentId, courseId) => {
    try {
      const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
      if (!sessionData || !sessionData.token) return;
      const response = await axios.get(`${apiUrl}/api/admin/payment-history/${studentId}?courseId=${courseId}&nocache=${Date.now()}`, {
        headers: { Authorization: `Bearer ${sessionData.token}` }
      });
      if (response.data.success) {
        const processedPayments = response.data.payments.map(payment => ({
          ...payment,
          amountpaid: Number(payment.amountpaid) || 0
        }));
        const processedDiscounts = response.data.discounts.map(discount => ({
          ...discount,
          discount_amount: Number(discount.discount_amount) || 0,
          new_fee: Number(discount.new_fee) || 0,
          original_fee: Number(discount.original_fee) || 0
        }));
        setPaymentHistory(processedPayments);
        setDiscountHistory(processedDiscounts);
      }
    } catch (err) {
      console.error('Error fetching payment history:', err);
    }
  };

  const handlePaymentChange = (e) => {
    const { id, value } = e.target;
    setPaymentForm((prev) => ({ ...prev, [id]: value }));
    setPaymentError('');
  };

  const handleDiscountChange = (e) => {
    const { id, value } = e.target;
    setDiscountForm((prev) => ({ ...prev, [id === 'discountAmount' ? 'discountAmount' : 'remark']: value }));
    setDiscountError('');
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    if (!paymentForm.amountpaid || !paymentForm.modeofpayment) {
      setPaymentError('Amount and mode of payment are required.');
      return;
    }
    try {
      const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
      if (!sessionData || !sessionData.token) return;
      const response = await axios.post(`${apiUrl}/api/admin/payments`, {
        studentId: selectedStudent.studentid,
        courseId: paymentForm.courseId,
        amountpaid: paymentForm.amountpaid,
        modeofpayment: paymentForm.modeofpayment,
        remark: paymentForm.remark
      }, {
        headers: { Authorization: `Bearer ${sessionData.token}` }
      });
      if (response.data.success) {
        setPaymentSuccess('Payment recorded successfully.');
        setTimeout(() => {
          closePaymentModal();
          window.location.reload();
        }, 2000);
      } else {
        setPaymentError(response.data.message || 'Failed to record payment');
      }
    } catch (err) {
      setPaymentError(err.response?.data?.message || 'Error recording payment');
    }
  };

  const handleDiscountSubmit = async (e) => {
    e.preventDefault();
    if (!discountForm.discountAmount) {
      setDiscountError('Discount amount is required.');
      return;
    }
    try {
      const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
      if (!sessionData || !sessionData.token) return;
      const originalFee = selectedStudent.originalFee;
      const discountAmount = parseFloat(discountForm.discountAmount);
      const newFee = originalFee - discountAmount;
      const response = await axios.post(`${apiUrl}/api/admin/discounts`, {
        studentId: selectedStudent.studentid,
        courseId: selectedStudent.courseid,
        batchId: selectedStudent.batchid,
        originalFee,
        discountAmount,
        newFee,
        remark: discountForm.remark
      }, {
        headers: { Authorization: `Bearer ${sessionData.token}` }
      });
      if (response.data.success) {
        setDiscountSuccess('Discount applied successfully.');
        setTimeout(() => {
          closeDiscountModal();
          window.location.reload();
        }, 2000);
      } else {
        setDiscountError(response.data.message || 'Failed to apply discount');
      }
    } catch (err) {
      setDiscountError(err.response?.data?.message || 'Error applying discount');
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (!editForm.amountpaid || !editForm.modeofpayment) {
      setEditError('Amount and mode of payment are required.');
      return;
    }

    try {
      const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
      if (!sessionData || !sessionData.token) return;

      const response = await axios.put(`${apiUrl}/api/admin/payments/${editForm.transactionId}`, {
        amountpaid: editForm.amountpaid,
        modeofpayment: editForm.modeofpayment,
        remark: editForm.remark
      }, {
        headers: { Authorization: `Bearer ${sessionData.token}` }
      });

      if (response.data.success) {
        setEditSuccess('Payment updated successfully.');
        setTimeout(() => {
          closeEditModal();
          fetchPaymentHistory(selectedStudent.studentid, selectedStudent.courseid);
        }, 2000);
      } else {
        setEditError(response.data.message || 'Failed to update payment');
      }
    } catch (err) {
      setEditError(err.response?.data?.message || 'Error updating payment');
    }
  };

  const handleEditDiscountSubmit = async (e) => {
    e.preventDefault();
    
    if (!editDiscountForm.discountAmount) {
      setEditDiscountError('Discount amount is required.');
      return;
    }

    try {
      const sessionData = JSON.parse(sessionStorage.getItem('adminSession'));
      if (!sessionData || !sessionData.token) return;

      const newFee = selectedStudent.originalFee - parseFloat(editDiscountForm.discountAmount);

      const response = await axios.put(`${apiUrl}/api/admin/discounts/${editDiscountForm.discountId}`, {
        discountAmount: editDiscountForm.discountAmount,
        newFee: newFee,
        remark: editDiscountForm.remark,
        studentId: selectedStudent.studentid,
        courseId: selectedStudent.courseid
      }, {
        headers: { Authorization: `Bearer ${sessionData.token}` }
      });

      if (response.data.success) {
        setEditDiscountSuccess('Discount updated successfully.');
        setTimeout(() => {
          closeEditDiscountModal();
          fetchPaymentHistory(selectedStudent.studentid, selectedStudent.courseid);
          window.location.reload();
        }, 2000);
      } else {
        setEditDiscountError(response.data.message || 'Failed to update discount');
      }
    } catch (err) {
      setEditDiscountError(err.response?.data?.message || 'Error updating discount');
    }
  };

  const generateReceipt = (payment) => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const primaryColor = '#009ab4';
    const pageWidth = 210;
    const margin = 10;
    const contentWidth = pageWidth - (2 * margin);

    doc.addImage('/assets/images/logo/logo.jpg', 'JPEG', margin, margin, 100, 30);
    doc.setFillColor(primaryColor);
    doc.rect(margin, 40, contentWidth, 20, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.text('IFM Edu Academy Pvt Ltd.', margin + 5, 46);
    doc.setFontSize(9);
    doc.text('VSK TOWER, Opp. TVS Jn, Ernakulam, Kerala - 682019', margin + 5, 51);
    doc.text('Email: enquiry@ifmedu.in | Phone: +91 90486 48777', margin + 5, 56);
    doc.setTextColor(0, 0, 0);

    doc.setFontSize(20);
    doc.setTextColor(primaryColor);
    doc.text('Receipt', margin, 70);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(12);

    const detailsY = 80;
    doc.text(`Receipt No: ${payment.transactionid}`, margin, detailsY);
    doc.text(`Date: ${new Date(payment.datepaid).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })} ${new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })} IST`, margin, detailsY + 10);
    doc.text(`Student Name: ${selectedStudent.firstName} ${selectedStudent.lastName}`, margin, detailsY + 20);
    doc.text(`Course: ${selectedStudent.courseName}`, margin, detailsY + 30);

    const feesY = detailsY + 45;
    doc.setFillColor(primaryColor);
    doc.rect(margin, feesY - 5, contentWidth, 10, 'F');
    doc.setTextColor(255, 255, 255);
    doc.text('Description', margin + 5, feesY + 2);
    doc.text('Amount (INR)', margin + 60, feesY + 2);
    doc.setTextColor(0, 0, 0);
    doc.setDrawColor(0);
    doc.line(margin, feesY + 5, pageWidth - margin, feesY + 5);

    doc.text('Fees Paid', margin + 5, feesY + 17);
    doc.text(`INR ${payment.amountpaid.toFixed(2)}`, margin + 60, feesY + 17);

    const additionalY = feesY + 35;
    doc.text(`Mode of Payment: ${payment.modeofpayment}`, margin, additionalY);
    const remarkY = additionalY + 10;
    doc.text(`Remark: ${payment.remark || 'None'}`, margin, remarkY);
    const totfeeY = remarkY + 10;
    doc.text('Total Fee:', margin, totfeeY + 10);
    doc.text(`INR ${(selectedStudent.originalFee || 0).toFixed(2)}`, margin +30, totfeeY + 10);
    const balfee = totfeeY + 10;
    doc.text('Balance:', margin, balfee + 10);
    doc.text(`INR ${(selectedStudent.balance || 0).toFixed(2)}`, margin + 30, balfee + 10);

    if ((selectedStudent.discountAmount || 0) > 0) {
      const discountY = balfee + 20;
      doc.text('Total Discount Applied:', margin, discountY);
      doc.text(`INR ${(selectedStudent.discountAmount || 0).toFixed(2)}`, margin + 60, discountY);
    }

    const footerY = 250;
    doc.setFillColor(primaryColor);
    doc.rect(margin, footerY, contentWidth, 20, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.text('Thank you for your payment! For any queries, contact us at enquiry@ifmedu.in or +91 90486 48777.', margin + 5, footerY + 5);
    doc.text('© 2025 IFM Edu Academy Pvt Ltd. All rights reserved.', margin + 5, footerY + 12);
    doc.save(`receipt_${payment.transactionid}.pdf`);
  };

  const generateDiscountReceipt = (discount) => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const primaryColor = '#009ab4';
    const pageWidth = 210;
    const margin = 10;
    const contentWidth = pageWidth - (2 * margin);

    doc.addImage('/assets/images/logo/logo.jpg', 'JPEG', margin, margin, 100, 30);
    doc.setFillColor(primaryColor);
    doc.rect(margin, 40, contentWidth, 20, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.text('IFM Edu Academy Pvt Ltd.', margin + 5, 46);
    doc.setFontSize(9);
    doc.text('VSK TOWER, Opp. TVS Jn, Ernakulam, Kerala - 682019', margin + 5, 51);
    doc.text('Email: enquiry@ifmedu.in | Phone: +91 90486 48777', margin + 5, 56);
    doc.setTextColor(0, 0, 0);

    doc.setFontSize(20);
    doc.setTextColor(primaryColor);
    doc.text('Discount Receipt', margin, 70);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(12);

    const detailsY = 80;
    doc.text(`Receipt No: DSC-${discount.id}`, margin, detailsY);
    doc.text(`Date: ${new Date(discount.date_applied).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })}`, margin, detailsY + 10);
    doc.text(`Student Name: ${selectedStudent.firstName} ${selectedStudent.lastName}`, margin, detailsY + 20);
    doc.text(`Course: ${selectedStudent.courseName}`, margin, detailsY + 30);

    const discountY = detailsY + 45;
    doc.setFillColor(primaryColor);
    doc.rect(margin, discountY - 5, contentWidth, 10, 'F');
    doc.setTextColor(255, 255, 255);
    doc.text('Description', margin + 5, discountY + 2);
    doc.text('Amount (INR)', margin + 60, discountY + 2);
    doc.setTextColor(0, 0, 0);
    doc.line(margin, discountY + 5, pageWidth - margin, discountY + 5);
    doc.text('Original Fee', margin + 5, discountY + 17);
    doc.text(`INR ${(discount.original_fee || 0).toFixed(2)}`, margin + 60, discountY + 17);
    doc.text('Discount Amount', margin + 5, discountY + 27);
    doc.text(`INR ${(discount.discount_amount || 0).toFixed(2)}`, margin + 60, discountY + 27);
    doc.text('New Fee', margin + 5, discountY + 37);
    doc.text(`INR ${(discount.new_fee || 0).toFixed(2)}`, margin + 60, discountY + 37);

    const remarkY = discountY + 50;
    doc.text(`Remark: ${discount.remark || 'None'}`, margin, remarkY);

    const footerY = 250;
    doc.setFillColor(primaryColor);
    doc.rect(margin, footerY, contentWidth, 20, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.text('Thank you for your enrollment! For queries, contact enquiry@ifmedu.in or +91 90486 48777.', margin + 5, footerY + 5);
    doc.text('© 2025 IFM Edu Academy Pvt Ltd. All rights reserved.', margin + 5, footerY + 12);
    doc.save(`discount_receipt_${discount.id}.pdf`);
  };

  const courseOptions = Array.from(
    new Map(students.map((s) => [s.courseid, s.courseName])).entries()
  ).sort((a, b) => (a[1] || '').localeCompare(b[1] || ''));

  const filteredStudents = students.filter((student) => {
    if (courseFilter && String(student.courseid) !== courseFilter) return false;
    if (statusFilter === 'pending' && !(student.balance > 0)) return false;
    if (statusFilter === 'paid' && !(student.balance <= 0)) return false;
    if (search) {
      const q = search.trim().toLowerCase();
      const haystack = [student.firstName, student.lastName, student.student_id, student.email, student.mobile]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body">
          <div className="breadcrumb-with-buttons mb-24 flex-between flex-wrap gap-8">
            <div className="breadcrumb mb-24">
              <ul className="flex-align gap-4">
                <li>
                  <Link to="/admin-dashboard" className="text-gray-200 fw-normal text-15 hover-text-main-600">
                    Home
                  </Link>
                </li>
                <li>
                  <span className="text-gray-500 fw-normal d-flex">
                    <i className="ph ph-caret-right" />
                  </span>
                </li>
                <li>
                  <span className="text-main-600 fw-normal text-15">Fees Manager</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="card mb-24">
            <div className="card-body">
              <div className="row g-16">
                <div className="col-md-4">
                  <label className="text-13 text-gray-500 mb-4 d-block">Search</label>
                  <input
                    type="text"
                    className="form-control py-9"
                    placeholder="Search name, student ID, email, or mobile..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <div className="col-md-4">
                  <label className="text-13 text-gray-500 mb-4 d-block">Course</label>
                  <select className="form-control py-9" value={courseFilter} onChange={(e) => setCourseFilter(e.target.value)}>
                    <option value="">All Courses</option>
                    {courseOptions.map(([id, name]) => (
                      <option key={id} value={id}>{name}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="text-13 text-gray-500 mb-4 d-block">Payment Status</label>
                  <select className="form-control py-9" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="">All</option>
                    <option value="pending">Balance Pending</option>
                    <option value="paid">Fully Paid</option>
                  </select>
                </div>
                <div className="col-12">
                  <div className="form-check mt-8">
                    <input
                      className="form-check-input border-gray-200 rounded-4"
                      type="checkbox"
                      id="showHiddenFees"
                      checked={showHidden}
                      onChange={(e) => setShowHidden(e.target.checked)}
                    />
                    <label className="form-check-label text-13 text-gray-500" htmlFor="showHiddenFees">
                      Show hidden students
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="card overflow-hidden">
            <div className="card-body p-0 overflow-x-auto">
              {loading && (
                <div className="alert alert-info mb-0" role="alert">
                  Loading students...
                </div>
              )}
              {error && (
                <div className="alert alert-danger mb-0" role="alert">
                  {error}
                </div>
              )}
              {!loading && !error && students.length === 0 && (
                <div className="alert alert-warning mb-0" role="alert">
                  No students found.
                </div>
              )}
              {!loading && !error && students.length > 0 && filteredStudents.length === 0 && (
                <div className="alert alert-warning mb-0" role="alert">
                  No students match your search/filters.
                </div>
              )}
              {!loading && !error && filteredStudents.length > 0 && (
                <table id="feesTable" className="table table-striped">
                  <thead>
                    <tr>
                      <th className="fixed-width">
                        <div className="form-check">
                          <input
                            className="form-check-input border-gray-200 rounded-4"
                            type="checkbox"
                            id="selectAll"
                          />
                        </div>
                      </th>
                      <th className="h6 text-gray-300">Student Name</th>
                      <th className="h6 text-gray-300">Student ID</th>
                      <th className="h6 text-gray-300">Course Joined</th>
                      <th className="h6 text-gray-300">Batch</th>
                      <th className="h6 text-gray-300">Total Fee</th>
                      <th className="h6 text-gray-300">Discount</th>
                      <th className="h6 text-gray-300">Fees Paid</th>
                      <th className="h6 text-gray-300">Balance</th>
                      <th className="h6 text-gray-300">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStudents.map((student) => (
                      <tr key={`${student.studentid}-${student.courseid}`}>
                        <td className="fixed-width">
                          <div className="form-check">
                            <input
                              className="form-check-input border-gray-200 rounded-4"
                              type="checkbox"
                            />
                          </div>
                        </td>
                        <td>
                          <span className="h6 mb-0 fw-medium text-gray-300">
                            {student.firstName} {student.lastName}
                          </span>
                        </td>
                        <td>
                          <span className="h6 mb-0 fw-medium text-gray-300">
                            {student.student_id || 'N/A'}
                          </span>
                        </td>
                        <td>
                          <span className="h6 mb-0 fw-medium text-gray-300">
                            {student.courseName.length > 30 ? `${student.courseName.substring(0, 30)}...` : student.courseName || 'Not Assigned'}
                          </span>
                        </td>
                        <td>
                          <span className="h6 mb-0 fw-medium text-gray-300">
                            {student.batchName.length > 30 ? `${student.batchName.substring(0, 30)}...` : student.batchName || 'Not Assigned'}
                          </span>
                        </td>
                        <td>
                          <span className="h6 mb-0 fw-medium text-gray-300">
                            ₹{(student.originalFee || 0).toFixed(2)}
                          </span>
                        </td>
                        <td>
                          <span className="h6 mb-0 fw-medium text-gray-300">
                            ₹{(student.discountAmount || 0).toFixed(2)}
                          </span>
                        </td>
                        <td>
                          <span className="h6 mb-0 fw-medium text-gray-300">
                            ₹{(student.feesPaid || 0).toFixed(2)}
                          </span>
                        </td>
                        <td>
                          <span className="h6 mb-0 fw-medium text-gray-300">
                            ₹{(student.balance || 0).toFixed(2)}
                          </span>
                        </td>
                        <td>
                          <button
                            className="bg-main-50 text-main-600 py-2 px-14 rounded-pill hover-bg-main-600 hover-text-white"
                            onClick={() => openPaymentModal(student)}
                          >
                            Pay Fee
                          </button>
                          <button
                            className="bg-success-50 text-success-600 py-2 px-14 rounded-pill hover-bg-success-600 hover-text-white ml-2"
                            onClick={() => openHistoryModal(student)}
                          >
                            View History
                          </button>
                          <button
                            className="bg-warning-50 text-warning-600 py-2 px-14 rounded-pill hover-bg-warning-600 hover-text-white ml-2"
                            onClick={() => openDiscountModal(student)}
                          >
                            Apply Discount
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="card-footer flex-between flex-wrap">
              <span className="text-gray-900">
                Showing {filteredStudents.length} of {students.length} entries
              </span>
              <ul className="pagination flex-align flex-wrap">
                <li className="page-item active">
                  <a
                    className="page-link h-44 w-44 flex-center text-15 rounded-8 fw-medium"
                    href="#"
                  >
                    1
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Pay Fee Modal */}
          {paymentModalOpen && (
            <div className="modal fade show d-block" tabIndex="-1" onClick={(e) => { if (e.target.className.includes('modal')) closePaymentModal(); }}>
              <div className="modal-dialog">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Pay Fee for {selectedStudent.firstName} {selectedStudent.lastName} - {selectedStudent.courseName}</h5>
                    <button type="button" className="btn-close" onClick={closePaymentModal}></button>
                  </div>
                  <div className="modal-body">
                    <p>Total Fee: ₹{(selectedStudent.originalFee || 0).toFixed(2)}</p>
                    <p>Balance Fee: ₹{(selectedStudent.balance || 0).toFixed(2)}</p>
                    {paymentError && <div className="alert alert-danger">{paymentError}</div>}
                    {paymentSuccess && <div className="alert alert-success">{paymentSuccess}</div>}
                    <form onSubmit={handlePaymentSubmit}>
                      <div className="mb-3">
                        <label htmlFor="amountpaid" className="form-label">Amount Paid</label>
                        <input
                          type="number"
                          className="form-control"
                          id="amountpaid"
                          value={paymentForm.amountpaid}
                          onChange={handlePaymentChange}
                          required
                        />
                      </div>
                      <div className="mb-3">
                        <label htmlFor="modeofpayment" className="form-label">Mode of Payment</label>
                        <select
                          id="modeofpayment"
                          className="form-select"
                          value={paymentForm.modeofpayment}
                          onChange={handlePaymentChange}
                          required
                        >
                          <option value="" disabled>Select mode</option>
                          <option value="Cash">Cash</option>
                          <option value="Gpay">Gpay</option>
                          <option value="Bank Transfer">Bank Transfer</option>
                          <option value="Cheque">Cheque</option>
                        </select>
                      </div>
                      <div className="mb-3">
                        <label htmlFor="remark" className="form-label">Remark</label>
                        <textarea
                          className="form-control"
                          id="remark"
                          value={paymentForm.remark}
                          onChange={handlePaymentChange}
                        ></textarea>
                      </div>
                      <button type="submit" className="btn btn-primary">Submit Payment</button>
                      <button type="button" className="btn btn-secondary ms-2" onClick={closePaymentModal}>Close</button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Apply Discount Modal */}
          {discountModalOpen && (
            <div className="modal fade show d-block" tabIndex="-1" onClick={(e) => { if (e.target.className.includes('modal')) closeDiscountModal(); }}>
              <div className="modal-dialog">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Apply Discount for {selectedStudent.firstName} {selectedStudent.lastName} - {selectedStudent.courseName}</h5>
                    <button type="button" className="btn-close" onClick={closeDiscountModal}></button>
                  </div>
                  <div className="modal-body">
                    <p>Original Fee: ₹{(selectedStudent.originalFee || 0).toFixed(2)}</p>
                    {discountError && <div className="alert alert-danger">{discountError}</div>}
                    {discountSuccess && <div className="alert alert-success">{discountSuccess}</div>}
                    <form onSubmit={handleDiscountSubmit}>
                      <div className="mb-3">
                        <label htmlFor="discountAmount" className="form-label">Discount Amount</label>
                        <input
                          type="number"
                          className="form-control"
                          id="discountAmount"
                          value={discountForm.discountAmount}
                          onChange={handleDiscountChange}
                          required
                        />
                      </div>
                      <div className="mb-3">
                        <label htmlFor="remark" className="form-label">Remark</label>
                        <textarea
                          className="form-control"
                          id="remark"
                          value={discountForm.remark}
                          onChange={handleDiscountChange}
                        ></textarea>
                      </div>
                      <button type="submit" className="btn btn-primary">Apply Discount</button>
                      <button type="button" className="btn btn-secondary ms-2" onClick={closeDiscountModal}>Close</button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Payment History Modal */}
          {historyModalOpen && (
            <div className="modal fade show d-block" tabIndex="-1" onClick={(e) => { if (e.target.className.includes('modal')) closeHistoryModal(); }}>
              <div className="modal-dialog modal-lg">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Payment and Discount History for {selectedStudent.firstName} {selectedStudent.lastName}</h5>
                    <button type="button" className="btn-close" onClick={closeHistoryModal}></button>
                  </div>
                  <div className="modal-body">
                    <h6>Payments</h6>
                    {paymentHistory.length === 0 && <div className="alert alert-warning">No payments found.</div>}
                    {paymentHistory.map((payment) => (
                      <div key={payment.id} className="mb-3 border p-2 rounded">
                        <p>Transaction ID: {payment.transactionid}</p>
                        <p>Amount: ₹{(payment.amountpaid || 0).toFixed(2)}</p>
                        <p>Mode: {payment.modeofpayment}</p>
                        <p>Remark: {payment.remark || 'None'}</p>
                        <p>Date: {new Date(payment.datepaid).toLocaleDateString()}</p>
                        <button className="btn btn-secondary me-2" onClick={() => generateReceipt(payment)}>Download Receipt</button>
                        <button className="btn btn-primary" onClick={() => openEditModal(payment)}>Edit Payment</button>
                      </div>
                    ))}
                    
                    <h6 className="mt-4">Discounts</h6>
                    {discountHistory.length === 0 && <div className="alert alert-warning">No discounts found.</div>}
                    {discountHistory.map((discount) => (
                      <div key={discount.id} className="mb-3 border p-2 rounded">
                        <p>Discount Amount: ₹{(discount.discount_amount || 0).toFixed(2)}</p>
                        <p>New Fee: ₹{(discount.new_fee || 0).toFixed(2)}</p>
                        <p>Remark: {discount.remark || 'None'}</p>
                        <p>Date: {new Date(discount.date_applied).toLocaleDateString()}</p>
                        <button className="btn btn-secondary me-2" onClick={() => generateDiscountReceipt(discount)}>Download Discount Receipt</button>
                        <button className="btn btn-primary" onClick={() => openEditDiscountModal(discount)}>Edit Discount</button>
                      </div>
                    ))}
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={closeHistoryModal}>Close</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Edit Payment Modal */}
          {editModalOpen && (
            <div className="modal fade show d-block" tabIndex="-1" onClick={(e) => { if (e.target.className.includes('modal')) closeEditModal(); }}>
              <div className="modal-dialog">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Edit Payment - {selectedPayment?.transactionid}</h5>
                    <button type="button" className="btn-close" onClick={closeEditModal}></button>
                  </div>
                  <div className="modal-body">
                    {editError && <div className="alert alert-danger">{editError}</div>}
                    {editSuccess && <div className="alert alert-success">{editSuccess}</div>}
                    <form onSubmit={handleEditSubmit}>
                      <div className="mb-3">
                        <label htmlFor="amountpaid" className="form-label">Amount Paid</label>
                        <input
                          type="number"
                          className="form-control"
                          id="amountpaid"
                          value={editForm.amountpaid}
                          onChange={handleEditChange}
                          required
                        />
                      </div>
                      <div className="mb-3">
                        <label htmlFor="modeofpayment" className="form-label">Mode of Payment</label>
                        <select
                          id="modeofpayment"
                          className="form-select"
                          value={editForm.modeofpayment}
                          onChange={handleEditChange}
                          required
                        >
                          <option value="" disabled>Select mode</option>
                          <option value="Cash">Cash</option>
                          <option value="Gpay">Gpay</option>
                          <option value="Bank Transfer">Bank Transfer</option>
                          <option value="Cheque">Cheque</option>
                        </select>
                      </div>
                      <div className="mb-3">
                        <label htmlFor="remark" className="form-label">Remark</label>
                        <textarea
                          className="form-control"
                          id="remark"
                          value={editForm.remark}
                          onChange={handleEditChange}
                        ></textarea>
                      </div>
                      <button type="submit" className="btn btn-primary">Update Payment</button>
                      <button type="button" className="btn btn-secondary ms-2" onClick={closeEditModal}>Cancel</button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Edit Discount Modal */}
          {editDiscountModalOpen && (
            <div className="modal fade show d-block" tabIndex="-1" onClick={(e) => { if (e.target.className.includes('modal')) closeEditDiscountModal(); }}>
              <div className="modal-dialog">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Edit Discount - {selectedDiscount?.id}</h5>
                    <button type="button" className="btn-close" onClick={closeEditDiscountModal}></button>
                  </div>
                  <div className="modal-body">
                    <p>Original Fee: ₹{(selectedStudent?.originalFee || 0).toFixed(2)}</p>
                    {editDiscountError && <div className="alert alert-danger">{editDiscountError}</div>}
                    {editDiscountSuccess && <div className="alert alert-success">{editDiscountSuccess}</div>}
                    <form onSubmit={handleEditDiscountSubmit}>
                      <div className="mb-3">
                        <label htmlFor="discountAmount" className="form-label">Discount Amount</label>
                        <input
                          type="number"
                          className="form-control"
                          id="discountAmount"
                          value={editDiscountForm.discountAmount}
                          onChange={handleEditDiscountChange}
                          required
                        />
                      </div>
                      <div className="mb-3">
                        <label htmlFor="remark" className="form-label">Remark</label>
                        <textarea
                          className="form-control"
                          id="remark"
                          value={editDiscountForm.remark}
                          onChange={handleEditDiscountChange}
                        ></textarea>
                      </div>
                      <div className="mb-3">
                        <p className="text-info">
                          New Fee after discount: ₹{(selectedStudent?.originalFee - parseFloat(editDiscountForm.discountAmount || 0)).toFixed(2)}
                        </p>
                      </div>
                      <button type="submit" className="btn btn-primary">Update Discount</button>
                      <button type="button" className="btn btn-secondary ms-2" onClick={closeEditDiscountModal}>Cancel</button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <Footer />
      </div>
    </>
  );
}

export default AdminFeesManager;